import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { useQuery } from '@tanstack/react-query';
import { fetchCountryRegions } from '@worldcoo/monolith/forms/regions';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger,
} from '../combobox';
import { cn } from '../../lib/utils';

type Option = { value: string; label: string };

type ProvinceFieldTexts = {
	search: string;
	noResults: string;
};

export default function ProvinceField({
	label,
	country,
	region,
	className,
	texts,
	optional,
}: {
	label: string;
	country?: string | undefined;
	region?: string | undefined;
	className?: string;
	texts: ProvinceFieldTexts;
	optional?: boolean;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);
	const [searchValue, setSearchValue] = useState('');

	const prevRegionRef = useRef(region);
	useEffect(() => {
		if (prevRegionRef.current !== region) {
			prevRegionRef.current = region;
			field.handleChange('');
		}
	}, [region, field]);

	const { data: regions = [] } = useQuery({
		queryKey: ['countries', country],
		queryFn: !country ? () => [] : () => fetchCountryRegions(country).then((data) => data ?? []),
	});

	const options = useMemo<Option[]>(
		() =>
			(regions.find((r) => r.code === region)?.provinces ?? []).map(({ code, name }) => ({
				value: code,
				label: name,
			})),
		[regions, region],
	);

	const selectedItem = useMemo(
		() => options.find((i) => i.value === field.state.value) ?? null,
		[options, field.state.value],
	);

	const filteredItems = useMemo(() => {
		if (!searchValue) return options;
		const search = searchValue.toLowerCase();
		return options.filter((item) => item.label.toLowerCase().includes(search));
	}, [options, searchValue]);

	return (
		<div
			className={cn(
				'flex flex-col gap-2',
				className,
				!formReadOnly && (!country || !region) && 'hidden',
				'[&_button]:transition-[color,border-color,padding,box-shadow]',
				formReadOnly &&
					'pointer-events-none [&_button]:border-transparent [&_button]:px-0 [&_button]:focus-visible:ring-0 [&_[data-slot=combobox-trigger]>svg]:hidden',
			)}
		>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<Combobox
				items={options}
				value={selectedItem}
				onValueChange={(item: Option | null) => field.handleChange(item?.value ?? '')}
				onInputValueChange={setSearchValue}
				isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
			>
				<ComboboxTrigger
					render={
						<button
							id={field.name}
							type="button"
							aria-invalid={hasFieldError(field.state.meta)}
							className={cn(
								'dark:bg-input/30 border-input focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive aria-invalid:ring-3 flex h-8 w-full items-center justify-between rounded-lg border bg-transparent px-2.5 text-sm transition-[color,border-color,padding,box-shadow] outline-none',
								// !selectedItem && 'text-muted-foreground',
							)}
						/>
					}
				>
					{selectedItem ? <span>{selectedItem.label}</span> : <span>{formReadOnly ? '-' : ''}</span>}
				</ComboboxTrigger>
				<ComboboxContent>
					<ComboboxInput showTrigger={false} placeholder={texts.search} />
					<ComboboxList>
						{filteredItems.map((item) => (
							<ComboboxItem key={item.value} value={item}>
								{item.label}
							</ComboboxItem>
						))}
					</ComboboxList>
					<ComboboxEmpty>{texts.noResults}</ComboboxEmpty>
				</ComboboxContent>
			</Combobox>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
